<template>
  <div>
    <div v-if="isLoading" class="center-center fade-in loading-overlay">
      <ion-spinner class="center-center spinner-large" name="lines" color="dark"></ion-spinner>
    </div>
    <ion-row v-if="!diagnosisMeasureReports?.length">
      <ion-col class="display-flex valign center-center no-programs">
        <div class="text-muted">
          No Diagnosis Considerations currently available.
        </div>
      </ion-col>
    </ion-row>
    <div v-else>
      <ion-row class="w-full">
        <diagnosis-consideration-page
          :measure_reports.prop="diagnosisMeasureReports"
          :patient_id="beneficiaryId"
          :beneficiary_id="beneficiaryId"
          :practitioner_id.prop="practitionerId"
          :valueset_loader_function.prop="valueSetLoaderFunction"
          :documentation_assistant_enabled="true"
          :can_edit.prop="getAttestationEditable"
          :document_references.prop="diagnosisDocumentReferences"
          :up_to_date_enabled.prop="canAccessUpToDate"
          :show_subcategory_title.prop="showSubcategoryTitle"
          :visit_note_upload_enabled.prop="canAccessVisitNoteUpload"
          :related_history_enabled.prop="canAccessRelatedHistory"
          :related_history_loader_function.prop="relatedHistoryLoaderFunction"
          @document_reference_change="onDocumentReferenceChange"
          @launchNotesModal="onModalInit"
        ></diagnosis-consideration-page>
      </ion-row>
    </div>
  </div>
</template>
<script>
import { mapActions, mapGetters } from "vuex";
import { engageFhirApi } from "../../../services/EngageFhirApi";
import ModalDxHistoryNotes from "@/components/Settings/Patient/ModalDxHistoryNotes";
import { getCurrentProfessional } from "@/services/Api/getCurrentProfessional";
import { Map } from "core-js";
import { organizeMeasureReportsBySubCategory } from "@engage/common-functions";

export default {
  name: "DiagnosisConsiderationWrapper",

  props: {
    beneficiaryId: {
      required: true,
      type: Number
    },
    showSubcategoryTitle: {
      required: false,
      type: Boolean,
      default: true
    },
  },

  data() {
    return {
      isSavingCounter: 0,
      isLoading: false,
      practitionerId: null,
      relatedHistory: new Map(),
    };
  },
  watch: {
    isSavingCounter(newVal) {
      this.$emit("doc-ref-saving-counter-updated", newVal);
    },
    isLoading(newVal) {
      this.$emit("doc-ref-loading", newVal);
    }
  },

  async mounted() {
    this.practitionerId = await this.getPractitionerId();
    await this.getDiagnosisConsiderations();
  },

  computed: {
    ...mapGetters({
      getAttestationEditable: "diagnosisConsiderations/getAttestationEditable",
      unsortedMeasureReports: "diagnosisConsiderations/getDiagnosisMeasureReports", 
      diagnosisDocumentReferences: "diagnosisConsiderations/getDiagnosisDocumentReferences",
      diagnosisConsiderationsPage: "appConfig/diagnosisConsiderationsPage",
      canAccessUpToDate: "appConfig/upToDateEnabled",
      canAccessVisitNoteUpload: "appConfig/dcVisitNoteUploadEnabled",
      canAccessRelatedHistory: "appConfig/relatedHistoryEnabled"
    }),
    
    diagnosisMeasureReports() {
      return organizeMeasureReportsBySubCategory(
        this.unsortedMeasureReports || [],
        this.diagnosisConsiderationsPage?.subCategorySortOrder ?? [],
        "Diagnosis Insights"
      );
    }
  },

  methods: {
    ...mapActions({
      getDiagnosisConsiderationData: "diagnosisConsiderations/fetchDCData",
      updateDocumentReference: "diagnosisConsiderations/updateDocumentReference"
    }),
    /**
     * Open Modal
     */
    onModalInit(ev) {
      this.$ionic.modalController
        .create({
          component: ModalDxHistoryNotes,
          mode: "ios",
          cssClass: "prime-modal-med",
          componentProps: {
            propsData: {
              title: "Consideration Notes and History",
              docDetails: ev.detail,
              practitionerId: this.practitionerId
            }
          },
          backdropDismiss: false
        })
        .then(m => m.present());
    },

    async onDocumentReferenceChange(e) {
      try {
        this.isSavingCounter++;
        const updatedDocRef = await engageFhirApi.post(`/DocumentReference`, e.detail);
        this.updateDocumentReference(updatedDocRef);
        this.isSavingCounter--;
      } catch (error) {
        this.isSavingCounter--;
        const displayErrorMessage = `There was a problem saving your status. ${
          error?.statusCode
        } ${error?.message ?? "Please try again later."}`;
        const toast = await this.$ionic.toastController.create({
          header: "Error!",
          message: displayErrorMessage,
          duration: 4000,
          position: "top"
        });
        toast.present();
      }
    },

    async getDiagnosisConsiderations() {
      try {
        this.isLoading = true;
        await this.getDiagnosisConsiderationData(this.beneficiaryId);
        this.isLoading = false;
      } catch (error) {
        khanSolo.error("Error fetching Diagnosis Considerations");
        khanSolo.error(error);
        const toast = await this.$ionic.toastController.create({
          header: "Failed to get documentation",
          message: "There was a problem getting your documentation.",
          duration: 7000,
          position: "top"
        });
        toast.present();
        this.isLoading = false;
      }
    },

    /** Get user's practitioner ID from auth token */
    async getPractitionerId() {
      const data = await getCurrentProfessional();
      return data.professional?.id;
    },
    async valueSetLoaderFunction(measureId) {
      try {
        return await engageFhirApi.get(`/ValueSet/measure/${measureId}`);
      } catch (error) {
        if ([error?.status, error?.statusCode].includes(404)) {
          khanSolo.log(`No ValueSet found for measure ${measureId}`);
        } else {
          khanSolo.error(`Error fetching ValueSet for measure ${measureId}`);
          khanSolo.error(error);
        }
        return [];
      }
    },
    async relatedHistoryLoaderFunction(
      beneficiaryId, measureReport
    ) {
      try {
        const [medicationHistory, labHistory, specialistVisits] = await Promise.all([
          engageFhirApi.put(`/visit-assistant/related-history/medication/${beneficiaryId}`,measureReport),
          engageFhirApi.put(`/visit-assistant/related-history/lab-vitals/${beneficiaryId}`,measureReport),
          engageFhirApi.put(`/visit-assistant/related-history/specialist-visits/${beneficiaryId}`,measureReport),
        ]);

        this.relatedHistory = { entry: [
          ...(medicationHistory?.entry || []),
          ...(labHistory?.entry || []),
          ...(specialistVisits?.entry || []),
        ].filter((e) => !!e)};

        return this.relatedHistory;
      } catch (error) {
        khanSolo.error(`Error fetching related history for beneficiary ${beneficiaryId}`, error);
      }
    }
  }
};
</script>
<style>
diagnosis-consideration-page {
  width: 100%;
}
.no-programs {
  height: 100px;
}
</style>
